import React, { useEffect, useMemo, useRef, useState } from "react";
import { midiToKey, playNote, audioSupported } from "../../lib/keyboardPlan";
import { invWalk, playMidiChord } from "../../lib/numberSenseV2";
import { Icon, Keys, Piano, TypingHint, MidiBadge, useTypingPiano } from "../numberSenseUi";
import useMidi from "../numberSense/useMidi";

const CAN_PLAY = audioSupported();

/* ============================================================
   Number Sense v2 — the guided flip walk, ChordWalkScreen's
   inversion twin: take the three chords a song leans on hardest
   (I, IV, V) through every position, one tone at a time. Stage 0
   builds root position the familiar way (root, 3rd, 5th); stages
   1 and 2 flip the bottom note up top and restack, so the slash
   symbol ("Dm/F") falls out of the flipping instead of being
   announced upfront.

   Octave-exact, like the chord stack: a "correct" tap is the
   exact midi in that voicing's stack, not just the right pitch
   class — that's the whole point of an inversion. Two misses on
   a tone lights up the exact key. Every stage ends with a brief
   reveal (symbol + inversion name) before Continue moves on; the
   third stage's reveal rolls into the next round. After all three
   chords are flipped every way, the finish screen auto-plays the
   full set of nine voicings in order, then hands off to the
   drills via onDone.
   ============================================================ */

const keyId = (midi) => {
  const k = midiToKey(midi);
  return `${k.name}${k.octave}`;
};

const InvWalkScreen = ({ type, keyName, onDone, onClose }) => {
  const walk = useMemo(() => invWalk(type.id, keyName), [type.id, keyName]);
  const rounds = walk.rounds;

  const [round, setRound] = useState(0);
  const [stage, setStage] = useState(0); // 0 root position, 1 first inv, 2 second inv
  const [tone, setTone] = useState(0); // next tone to press, bottom to top
  const [misses, setMisses] = useState(0);
  const [wrong, setWrong] = useState(false);
  const [revealed, setRevealed] = useState(false); // this stage's stack is complete

  const finished = round >= rounds.length;
  const current = finished ? null : rounds[round];
  const currentStage = finished ? null : current.stages[stage];

  // Keys lit on the piano: the tones already placed this stage, all three
  // held over through the reveal so the shape stays visible while it's named.
  const placed = useMemo(() => {
    if (finished) return new Set();
    const n = revealed ? currentStage.stack.length : tone;
    return new Set(currentStage.stack.slice(0, n).map((t) => keyId(t.midi)));
  }, [currentStage, tone, revealed, finished]);

  const playedFinish = useRef(false);

  // The finale: once every chord is flipped every way, hear all nine
  // voicings in walk order — guarded so it only fires on entry.
  useEffect(() => {
    if (finished && CAN_PLAY && !playedFinish.current) {
      playedFinish.current = true;
      const all = rounds.flatMap((r) => r.stages);
      all.forEach((st, i) => playMidiChord(st.stack.map((t) => t.midi), 500, i * 0.5));
    }
  }, [finished, rounds]);

  const press = (k) => {
    if (finished || revealed) return;
    const expected = currentStage.stack[tone];
    if (k.midi === expected.midi) {
      setWrong(false);
      setMisses(0);
      if (tone === currentStage.stack.length - 1) {
        // bottom, middle, and top are all down — reveal what just got built
        setRevealed(true);
        if (CAN_PLAY) {
          window.setTimeout(
            () => playMidiChord(currentStage.stack.map((t) => t.midi)),
            250
          );
        }
      } else {
        setTone((t) => t + 1);
      }
    } else {
      setWrong(true);
      setMisses((m) => m + 1);
    }
  };

  const advance = () => {
    if (stage === current.stages.length - 1) {
      setRound((r) => r + 1);
      setStage(0);
    } else {
      setStage((s) => s + 1);
    }
    setTone(0);
    setMisses(0);
    setWrong(false);
    setRevealed(false);
  };

  const resetAll = () => {
    playedFinish.current = false;
    setRound(0);
    setStage(0);
    setTone(0);
    setMisses(0);
    setWrong(false);
    setRevealed(false);
  };

  // Typing keyboard and MIDI feed the same walk (Piano taps sound themselves;
  // typing needs the note played here). Both go quiet during the reveal
  // pause and once the whole set is flipped.
  const active = !finished && !revealed;
  const midi = useMidi(press, active);
  useTypingPiano((k) => {
    playNote(k.midi);
    press(k);
  }, active);

  return (
    <div className="ns-root">
      <div className="ns-app">
        <div className="subbar">
          <button className="iconbtn" onClick={onClose} aria-label="back">
            <Icon name="back" />
          </button>
          <div className="subbar-title">flip: {walk.label}</div>
        </div>
        <div className="scroll">
          {/* the three chords, each filling in as its three positions land */}
          <div className="teach-chips v2-walk-chips">
            {rounds.map((r, i) => {
              const completed = i < round;
              return (
                <span
                  className={`teach-chip ${completed ? "" : "v2-chip-dim"}`}
                  key={`${r.roman}-${i}`}
                >
                  <b className="mono">{r.roman}</b>
                  <span>{r.symbol}</span>
                </span>
              );
            })}
          </div>

          {!finished ? (
            <>
              <div className="v2-walk-banner">
                {!revealed ? (
                  <>
                    <div className="qkind">
                      chord {round + 1} of {rounds.length} · {currentStage.invName}
                    </div>
                    {stage === 0 && tone === 0 && (
                      <div className="qprompt">
                        build <b>{current.symbol}</b> — start on its root,{" "}
                        <b>{currentStage.stack[0].name}</b>
                      </div>
                    )}
                    {stage === 0 && tone === 1 && (
                      <div className="qprompt">
                        add the 3rd — <b>{currentStage.stack[1].name}</b>
                      </div>
                    )}
                    {stage === 0 && tone === 2 && (
                      <div className="qprompt">
                        add the 5th — <b>{currentStage.stack[2].name}</b>
                      </div>
                    )}
                    {stage === 1 && tone === 0 && (
                      <div className="qprompt">
                        now flip it — the 3rd, <b>{currentStage.bass}</b>, goes
                        on the bottom
                      </div>
                    )}
                    {stage === 2 && tone === 0 && (
                      <div className="qprompt">
                        flip once more — the 5th, <b>{currentStage.bass}</b>,
                        takes the bottom
                      </div>
                    )}
                    {stage > 0 && tone === 1 && (
                      <div className="qprompt">
                        then <b>{currentStage.stack[1].name}</b>
                      </div>
                    )}
                    {stage > 0 && tone === 2 && (
                      <div className="qprompt">
                        and the {currentStage.stack[2].role} lands on top —{" "}
                        <b>{currentStage.stack[2].name}</b>
                      </div>
                    )}
                    {wrong && (
                      <div className="v2-walk-msg">
                        not quite — same three notes, new bottom
                        {misses >= 2 ? " (it's glowing now)" : ""}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="qkind">
                      {stage === 0 ? "that's the chord" : "that's the flip"}
                    </div>
                    <div className="qprompt">
                      <b>{currentStage.symbol}</b> — {currentStage.invName}
                    </div>
                    {stage > 0 && (
                      <div className="qprompt-sub muted">
                        same notes as {currentStage.baseSymbol}, with the{" "}
                        {currentStage.stack[0].role} on the bottom
                      </div>
                    )}
                    <button className="btn" onClick={advance}>
                      Continue
                    </button>
                  </>
                )}
              </div>
              <Piano
                pressed={new Set()}
                exactKeys={placed}
                exactHint={
                  !revealed && misses >= 2
                    ? new Set([keyId(currentStage.stack[tone].midi)])
                    : null
                }
                onPress={press}
              />
              <TypingHint midiStatus={midi.status} />
              <MidiBadge status={midi.status} devices={midi.devices} />
            </>
          ) : (
            <>
              <div className="v2-walk-banner">
                <div className="qkind">all three flipped</div>
                <div className="qprompt">
                  you just played I, IV and V of <b>{walk.label}</b> in every
                  position
                </div>
                <div className="qprompt-sub muted">
                  a slash chord is just one of these — the name after the
                  slash is the bottom note
                </div>
              </div>
              <div className="v2-walk-actions">
                <button className="btn" onClick={onDone}>
                  Drill the flips
                </button>
                <button className="btn ghost" onClick={resetAll}>
                  Flip them again
                </button>
              </div>
            </>
          )}

          <div className="footnote">
            <Keys />
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvWalkScreen;
